import { useState, useEffect } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import Navbar from "../../../components/feature/Navbar";
import Footer from "../../../components/feature/Footer";
import { allProperties } from "../../../mocks/properties";

const typeDescriptions: Record<string, string> = {
  "Assisted Living":
    "This registered assisted living facility provides 24-hour personal care and support for adults with complex needs, including elderly residents and those with physical or cognitive disabilities. The property is purpose-built to CQC standards, featuring fully accessible communal spaces, en-suite bedrooms, a nurse call system, and a fully equipped commercial kitchen. It is operated under a Full Repairing and Insuring (FRI) lease with a UK-registered housing association, which is solely responsible for all maintenance, regulatory compliance, and staffing costs — removing all operational burden from the investor.",
  "Supported Housing":
    "This supported housing scheme provides semi-independent living for adults requiring low to medium levels of daily support, including individuals transitioning from hospital care, those with learning disabilities, or adults recovering from mental health conditions. The property is delivered under a long-term lease agreement with a regulated housing association, which manages all tenancy arrangements, staffing, and property maintenance under a Full Repairing and Insuring framework. Rental income is underpinned by housing benefit entitlements, providing a highly resilient income stream for investors.",
  "Sheltered Housing":
    "This sheltered housing scheme provides a safe, managed residential environment for adults seeking independence with the security of on-site support. Properties feature individual self-contained units with communal facilities including a residents' lounge, laundry, and landscaped outdoor space. The scheme operates under a long-term lease with a registered social landlord, with rents subject to periodic index-linked review. The housing association manages all facilities, maintenance, and resident welfare — ensuring a fully passive investment structure for Thorpe Dellow investors.",
};

const leaseDetails: Record<string, { label: string; desc: string }[]> = {
  "FRI Lease": [
    { label: "Lease Type", desc: "Full Repairing & Insuring (FRI)" },
    { label: "Maintenance", desc: "Entirely tenant's responsibility" },
    { label: "Rent Review", desc: "Annual, linked to CPI index" },
    { label: "Break Clause", desc: "No landlord break clauses" },
  ],
  "25-Year Lease": [
    { label: "Lease Type", desc: "Long-term 25-Year Occupational Lease" },
    { label: "Maintenance", desc: "Full repairing obligation on tenant" },
    { label: "Rent Review", desc: "5-yearly upward-only rent review" },
    { label: "Break Clause", desc: "No break within first 10 years" },
  ],
  "Fully Leased": [
    { label: "Lease Type", desc: "Full Repairing & Insuring (FRI)" },
    { label: "Occupancy", desc: "100% — currently fully income-producing" },
    { label: "Rent Review", desc: "Annual CPI + 1% indexed increase" },
    { label: "Security", desc: "First legal charge held by trustee" },
  ],
  "CPI+1% Rent": [
    { label: "Lease Type", desc: "Assured tenancy with rent schedule" },
    { label: "Rent Review", desc: "CPI + 1% per annum, indexed annually" },
    { label: "Maintenance", desc: "Structural repairs landlord; interior tenant" },
    { label: "Break Clause", desc: "Rolling break after Year 5" },
  ],
};

const typeHighlights: Record<string, string[]> = {
  "Assisted Living": [
    "CQC-registered purpose-built facility",
    "24-hour care provision under FRI lease",
    "All staffing & operations managed by housing association",
    "En-suite accommodation for all residents",
    "Nurse call and CCTV security systems installed",
    "Compliant with Building Regulations Part M (Accessibility)",
  ],
  "Supported Housing": [
    "Semi-independent living scheme for vulnerable adults",
    "Housing benefit-backed rental income stream",
    "Long-term lease with registered social landlord",
    "All maintenance and tenancy management by operator",
    "Purpose-built to Homes England design standards",
    "CIH-accredited housing provider tenancy management",
  ],
  "Sheltered Housing": [
    "Independent living with managed on-site support",
    "Self-contained en-suite or studio units",
    "Communal lounge, laundry, and landscaped gardens",
    "Index-linked rent reviews (CPI-based)",
    "Housing association managed — fully passive investment",
    "Regular CQC and local authority compliance audits",
  ],
};

export default function PropertyDetailPage() {
  const { id } = useParams<{ id: string }>();
  const property = allProperties.find((p) => p.id === Number(id));
  const [formData, setFormData] = useState({ name: "", email: "", phone: "", message: "" });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState("");
  const [showStickyBar, setShowStickyBar] = useState(false);
  const [enquireVisible, setEnquireVisible] = useState(false);

  useEffect(() => {
    const handleScroll = () => setShowStickyBar(window.scrollY > 350);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const section = document.getElementById("enquire");
    if (!section) return;
    const observer = new IntersectionObserver(
      ([entry]) => setEnquireVisible(entry.isIntersecting),
      { rootMargin: "0px 0px -80px 0px", threshold: 0 }
    );
    observer.observe(section);
    return () => { observer.disconnect(); };
  }, []);

  if (!property) return <Navigate to="/properties" replace />;

  const description = typeDescriptions[property.type] ?? typeDescriptions["Assisted Living"];
  const highlights = typeHighlights[property.type] ?? typeHighlights["Assisted Living"];
  const lease = leaseDetails[property.tag] ?? leaseDetails["FRI Lease"];

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!formData.name || !formData.email) { setFormError("Please fill in all required fields."); return; }
    if (formData.message.length > 500) { setFormError("Message must be 500 characters or fewer."); return; }
    setFormError("");
    setSubmitting(true);
    const body = new URLSearchParams();
    body.append("name", formData.name);
    body.append("email", formData.email);
    body.append("phone", formData.phone);
    body.append("message", formData.message);
    body.append("propertyId", String(property.id));
    body.append("propertyTitle", property.title);
    try {
      await fetch("https://readdy.ai/api/form/d740ugj41deorl02ek30", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      setSubmitted(true);
    } catch {
      setFormError("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const otherProperties = allProperties.filter((p) => p.id !== property.id && p.type === property.type).slice(0, 3);

  return (
    <main className="min-h-screen bg-[#EEF2F9] pb-20 lg:pb-0">
      <Navbar />

      {/* Hero */}
      <section
        className="relative w-full overflow-hidden"
        style={{
          minHeight: 500,
          backgroundImage: `url('${property.image}')`,
          backgroundSize: "cover",
          backgroundPosition: "center top",
          backgroundRepeat: "no-repeat",
        }}
      >
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(7,26,46,0.55) 0%, rgba(7,26,46,0.82) 100%)" }} />
        <div className="absolute left-0 top-0 bottom-0 w-[3px]" style={{ background: "linear-gradient(to bottom, transparent, #C9A84C 30%, #E2C27A 60%, #C9A84C 85%, transparent)" }} />

        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8 pt-32 pb-14">
          {/* Breadcrumb */}
          <div className="flex items-center gap-2 text-xs text-white/50 mb-8">
            <Link to="/" className="hover:text-white/80 transition-colors cursor-pointer">Home</Link>
            <i className="ri-arrow-right-s-line leading-none" />
            <Link to="/properties" className="hover:text-white/80 transition-colors cursor-pointer">Properties</Link>
            <i className="ri-arrow-right-s-line leading-none" />
            <span className="text-white/80">{property.title}</span>
          </div>

          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 md:gap-8">
            <div>
              <div className="flex flex-wrap items-center gap-2 mb-4">
                <span className="bg-[#C9A84C] text-[#071A2E] text-[10px] font-bold uppercase tracking-wider px-3 py-1 rounded-full">{property.tag}</span>
                <span className="bg-white/15 text-white text-[10px] font-semibold uppercase tracking-wider px-3 py-1 rounded-full backdrop-blur-sm">{property.type}</span>
              </div>
              <h1 className="text-2xl md:text-5xl font-bold text-white mb-3 leading-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
                {property.title}
              </h1>
              <div className="flex items-center gap-2 text-white/70 text-sm">
                <div className="w-4 h-4 flex items-center justify-center"><i className="ri-map-pin-2-fill text-[#C9A84C] leading-none" /></div>
                {property.location}
              </div>
            </div>

            {/* Key stats row */}
            <div className="flex flex-wrap gap-2 md:gap-3">
              {[
                { icon: "ri-money-pound-circle-line", label: "Asset Value", value: property.price.replace(" Asset Value", "") },
                { icon: "ri-home-2-line", label: "Units", value: `${property.beds} Beds` },
                { icon: "ri-layout-line", label: "Floor Area", value: `${property.sqft.toLocaleString()} sqft` },
              ].map((s) => (
                <div key={s.label} className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl px-3 md:px-5 py-2.5 md:py-3 text-center min-w-[90px]">
                  <div className="w-5 h-5 flex items-center justify-center mx-auto mb-1 text-[#C9A84C]">
                    <i className={`${s.icon} text-sm leading-none`} />
                  </div>
                  <p className="text-white font-bold text-sm leading-none" style={{ fontFamily: "'Playfair Display', serif" }}>{s.value}</p>
                  <p className="text-white/50 text-[10px] mt-0.5 uppercase tracking-wide">{s.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 py-14">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">

          {/* LEFT — Details */}
          <div className="lg:col-span-2 flex flex-col gap-10">

            {/* Description */}
            <div className="bg-white rounded-2xl p-8">
              <h2 className="text-xl font-bold text-[#071A2E] mb-5" style={{ fontFamily: "'Playfair Display', serif" }}>
                Property Overview
              </h2>
              <p className="text-slate-600 text-sm leading-relaxed">{description}</p>
            </div>

            {/* Highlights */}
            <div className="bg-white rounded-2xl p-8">
              <h2 className="text-xl font-bold text-[#071A2E] mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
                Key Highlights
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {highlights.map((h) => (
                  <div key={h} className="flex items-start gap-3 bg-[#EEF2F9] rounded-xl px-4 py-3">
                    <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-check-double-line text-[#C9A84C] text-sm leading-none" />
                    </div>
                    <span className="text-slate-600 text-xs leading-relaxed">{h}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Lease Details */}
            <div className="bg-white rounded-2xl p-8">
              <h2 className="text-xl font-bold text-[#071A2E] mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
                Lease Structure
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {lease.map((l) => (
                  <div key={l.label} className="border border-slate-100 rounded-xl p-4">
                    <p className="text-[#C9A84C] text-[10px] font-bold uppercase tracking-widest mb-1">{l.label}</p>
                    <p className="text-[#071A2E] text-sm font-semibold">{l.desc}</p>
                  </div>
                ))}
              </div>
              <div className="mt-6 bg-[#071A2E] rounded-xl px-5 py-4 flex items-start gap-3">
                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5 text-[#C9A84C]">
                  <i className="ri-shield-check-line text-sm leading-none" />
                </div>
                <p className="text-slate-300 text-xs leading-relaxed">
                  All leases are structured under a Full Repairing &amp; Insuring framework. Investor capital is secured by a first legal charge over the property, held by an independent Security Trustee on behalf of all noteholders.
                </p>
              </div>
            </div>

            {/* Investment connection */}
            <div
              className="rounded-2xl p-8 relative overflow-hidden"
              style={{ background: "linear-gradient(135deg, #071A2E 0%, #0D2B4A 100%)" }}
            >
              <div className="absolute top-0 right-0 w-48 h-48 rounded-full opacity-10 pointer-events-none" style={{ background: "radial-gradient(circle, #C9A84C, transparent)" }} />
              <span className="text-[#C9A84C] text-[10px] font-bold uppercase tracking-widest block mb-3">Investment Link</span>
              <h3 className="text-xl font-bold text-white mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>
                Part of the Thorpe Dellow Social Housing Structured Product
              </h3>
              <p className="text-slate-400 text-sm leading-relaxed mb-5">
                This asset forms part of the underlying property portfolio backing the Thorpe Dellow 5-year structured note — targeting 10.00% per annum, distributed monthly to investors.
              </p>
              <div className="flex flex-wrap gap-3">
                <Link to="/social-housing" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#C9A84C] text-[#071A2E] text-xs font-bold uppercase tracking-wide cursor-pointer whitespace-nowrap hover:bg-[#E2C27A] transition-colors">
                  <i className="ri-file-text-line leading-none" />
                  View Full Product
                </Link>
                <Link to="/contact" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl border border-white/25 text-white text-xs font-semibold cursor-pointer whitespace-nowrap hover:bg-white/8 transition-colors">
                  <i className="ri-send-plane-line leading-none" />
                  Enquire Now
                </Link>
              </div>
            </div>
          </div>

          {/* RIGHT — Sticky Summary + Form */}
          <div className="flex flex-col gap-6">
            {/* Summary Card */}
            <div className="bg-white rounded-2xl p-6 border border-slate-100 lg:sticky lg:top-24">
              <div className="mb-4 pb-4 border-b border-slate-100">
                <p className="text-[#C9A84C] text-[10px] font-bold uppercase tracking-widest mb-1">Asset Value</p>
                <p className="text-3xl font-bold text-[#071A2E]" style={{ fontFamily: "'Playfair Display', serif" }}>
                  {property.price.replace(" Asset Value", "")}
                </p>
                <p className="text-slate-400 text-xs mt-0.5">{property.location}</p>
              </div>

              <div className="flex flex-col gap-3 mb-6">
                {[
                  { icon: "ri-building-4-line", label: "Property Type", value: property.type },
                  { icon: "ri-home-2-line", label: "Residential Units", value: `${property.beds} beds` },
                  { icon: "ri-layout-line", label: "Total Floor Area", value: `${property.sqft.toLocaleString()} sqft` },
                  { icon: "ri-file-list-3-line", label: "Lease Structure", value: property.tag },
                  { icon: "ri-map-pin-line", label: "Location", value: property.location },
                ].map((r) => (
                  <div key={r.label} className="flex items-center justify-between py-2 border-b border-slate-50">
                    <div className="flex items-center gap-2 text-slate-500">
                      <div className="w-4 h-4 flex items-center justify-center text-[#C9A84C]">
                        <i className={`${r.icon} text-xs leading-none`} />
                      </div>
                      <span className="text-xs">{r.label}</span>
                    </div>
                    <span className="text-xs font-semibold text-[#071A2E]">{r.value}</span>
                  </div>
                ))}
              </div>

              <div className="flex flex-col gap-2.5">
                <a
                  href="#enquire"
                  className="flex items-center justify-center gap-2 py-3 rounded-xl bg-[#071A2E] text-white text-xs font-bold uppercase tracking-wide cursor-pointer whitespace-nowrap hover:bg-[#0D2B4A] transition-colors"
                >
                  <i className="ri-send-plane-line leading-none" />
                  Enquire About This Asset
                </a>
                <Link
                  to="/contact"
                  className="flex items-center justify-center gap-2 py-3 rounded-xl border border-[#C9A84C]/40 text-[#C9A84C] text-xs font-semibold cursor-pointer whitespace-nowrap hover:bg-[#C9A84C]/8 transition-colors"
                >
                  <i className="ri-send-plane-line leading-none" />
                  Enquire Now
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Enquiry Form */}
      <section id="enquire" className="bg-white py-16 md:py-20">
        <div className="max-w-3xl mx-auto px-4 md:px-8">
          <div className="text-center mb-10">
            <span className="text-[#C9A84C] text-xs font-semibold uppercase tracking-widest block mb-3">Asset Enquiry</span>
            <h2 className="text-3xl font-bold text-[#071A2E]" style={{ fontFamily: "'Playfair Display', serif" }}>
              Enquire About {property.title}
            </h2>
            <p className="text-slate-500 text-sm mt-3 max-w-md mx-auto">
              Our investment team will respond within 1 business day with full asset documentation and investment details.
            </p>
          </div>

          {submitted ? (
            <div className="text-center py-16 bg-[#EEF2F9] rounded-2xl">
              <div className="w-16 h-16 flex items-center justify-center rounded-full mx-auto mb-4" style={{ background: "linear-gradient(135deg, rgba(201,168,76,0.2), rgba(201,168,76,0.05))", border: "1.5px solid rgba(201,168,76,0.4)" }}>
                <i className="ri-check-double-line text-[#C9A84C] text-2xl leading-none" />
              </div>
              <h3 className="font-bold text-[#071A2E] text-xl mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>Enquiry Received</h3>
              <p className="text-slate-500 text-sm max-w-sm mx-auto">Our team will be in touch within 1 business day with full details on {property.title}.</p>
              <Link to="/properties" className="inline-flex items-center gap-2 mt-6 px-6 py-3 rounded-full bg-[#071A2E] text-white text-sm font-semibold hover:bg-[#0D2B4A] transition-colors cursor-pointer whitespace-nowrap">
                <i className="ri-arrow-left-line leading-none" />
                Back to Portfolio
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} data-readdy-form className="bg-[#EEF2F9] rounded-2xl p-8 flex flex-col gap-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-xs font-semibold text-[#071A2E] mb-1.5">Full Name *</label>
                  <input type="text" name="name" value={formData.name} onChange={handleChange} required placeholder="Jane Smith" className="w-full bg-white border border-slate-200 rounded-lg px-4 py-3 text-sm text-slate-700 focus:outline-none focus:border-[#C9A84C] transition-colors" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-[#071A2E] mb-1.5">Email Address *</label>
                  <input type="email" name="email" value={formData.email} onChange={handleChange} required placeholder="jane@example.com" className="w-full bg-white border border-slate-200 rounded-lg px-4 py-3 text-sm text-slate-700 focus:outline-none focus:border-[#C9A84C] transition-colors" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-[#071A2E] mb-1.5">Phone Number</label>
                <input type="tel" name="phone" value={formData.phone} onChange={handleChange} placeholder="+44 (0)7700 000000" className="w-full bg-white border border-slate-200 rounded-lg px-4 py-3 text-sm text-slate-700 focus:outline-none focus:border-[#C9A84C] transition-colors" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-[#071A2E] mb-1.5">Message <span className="font-normal text-slate-400">(max 500 characters)</span></label>
                <textarea name="message" value={formData.message} onChange={handleChange} rows={4} maxLength={500} placeholder={`I would like more information about ${property.title}...`} className="w-full bg-white border border-slate-200 rounded-lg px-4 py-3 text-sm text-slate-700 focus:outline-none focus:border-[#C9A84C] transition-colors resize-none" />
                <p className="text-right text-xs text-slate-400 mt-1">{formData.message.length}/500</p>
              </div>
              <div className="bg-white border border-slate-100 rounded-lg px-4 py-3">
                <p className="text-xs text-slate-500 leading-relaxed">By submitting this enquiry, you confirm you are a Certified High Net Worth Individual or Sophisticated Investor. Capital at risk. Thorpe Dellow complies with Section 21 FSMA 2000.</p>
              </div>
              {formError && <p className="text-red-500 text-xs bg-red-50 border border-red-200 rounded-lg px-4 py-3">{formError}</p>}
              <button type="submit" disabled={submitting} className="w-full py-4 rounded-xl bg-[#071A2E] text-white font-semibold text-sm hover:bg-[#0D2B4A] transition-colors cursor-pointer disabled:opacity-60 whitespace-nowrap">
                {submitting ? "Submitting..." : "Submit Enquiry"}
              </button>
            </form>
          )}
        </div>
      </section>

      {/* Related Properties */}
      {otherProperties.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 md:px-8 py-14">
          <h2 className="text-2xl font-bold text-[#071A2E] mb-8" style={{ fontFamily: "'Playfair Display', serif" }}>
            Similar {property.type} Assets
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            {otherProperties.map((p) => (
              <Link key={p.id} to={`/properties/${p.id}`} className="bg-white rounded-2xl overflow-hidden group hover:-translate-y-1 transition-transform duration-300 cursor-pointer">
                <div className="h-44 overflow-hidden">
                  <img src={p.image} alt={p.title} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
                </div>
                <div className="p-5">
                  <p className="text-xs text-slate-400 mb-1">{p.type}</p>
                  <h3 className="font-bold text-[#071A2E] text-sm mb-1" style={{ fontFamily: "'Playfair Display', serif" }}>{p.title}</h3>
                  <p className="text-[#C9A84C] font-bold text-sm">{p.price}</p>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* ── Mobile Sticky Enquire Bar ── */}
      {!submitted && (
        <div
          className={`lg:hidden fixed bottom-0 left-0 right-0 z-50 transition-transform duration-300 ease-in-out ${
            showStickyBar && !enquireVisible ? "translate-y-0" : "translate-y-full"
          }`}
          style={{ boxShadow: "0 -6px 32px rgba(7,26,46,0.30)" }}
        >
          <div
            className="bg-[#071A2E] border-t-2 flex items-center gap-3 px-4 py-3"
            style={{ borderColor: "rgba(201,168,76,0.35)" }}
          >
            {/* Property mini-info */}
            <div className="flex-1 min-w-0">
              <p className="text-white font-semibold text-sm leading-snug truncate">{property.title}</p>
              <p className="text-[#C9A84C] text-xs font-bold mt-0.5">
                {property.price.replace(" Asset Value", "")}
              </p>
            </div>

            {/* Contact pill */}
            <Link
              to="/contact"
              className="flex items-center gap-1.5 px-3 py-2.5 rounded-xl border border-[#C9A84C]/40 text-[#C9A84C] text-xs font-semibold whitespace-nowrap cursor-pointer flex-shrink-0 hover:bg-[#C9A84C]/10 transition-colors"
            >
              <i className="ri-phone-line leading-none" />
              Contact
            </Link>

            {/* Enquire Now CTA */}
            <a
              href="#enquire"
              className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-xs font-bold uppercase tracking-wide whitespace-nowrap cursor-pointer flex-shrink-0 hover:opacity-90 transition-opacity"
              style={{ background: "linear-gradient(135deg, #C9A84C 0%, #E2C27A 100%)", color: "#071A2E" }}
            >
              <i className="ri-send-plane-fill leading-none" />
              Enquire Now
            </a>
          </div>
          {/* iOS safe area spacer */}
          <div className="bg-[#071A2E]" style={{ height: "env(safe-area-inset-bottom, 0px)" }} />
        </div>
      )}

      <Footer />
    </main>
  );
}
